// Standby flow pulse
// ------------------
// While a workflow is idle (not executing), a soft gray glow dot glides along
// each connection from its source node toward its target and repeats. This is
// a passive, at-a-glance cue for the *direction* of the flow — no run needed.
//
// Pure render helper: the caller passes React.createElement (`h`) so this module
// never imports React itself. The travelling dot is driven by SVG SMIL
// (animateMotion along the connection's own path) which is GPU-cheap and stays
// perfectly glued to the curve, including bends.

// Travel time for one source->target trip. Slow + smooth so it reads as a hint,
// not a busy "running" animation (running flow uses the gold marching dashes).
var TRAVEL_DUR = '2.6s';

// Phase the dots so a chain of connections reads as one pulse marching
// downstream instead of every line blinking in lockstep. A small negative
// begin starts each dot partway through its loop. Wraps every 4 so long flows
// keep a steady rhythm.
function travelBegin(index) {
  return (-((index % 4) * (2.6 / 4))).toFixed(2) + 's';
}

// Shared timing for the opacity + radius "pulse": invisible at both endpoints so
// the dot appears to emerge from the source and dissolve into the target.
var PULSE_KEYTIMES = '0;0.16;0.8;1';

// Tint the travelling dot to match the connector it rides. Condition YES/NO and
// no-wait edges are color-coded, so a gray dot on a blue/pink line reads as a
// mismatch — each one gets its own gradient (see renderStandbyFlowGradient).
function gradientIdForConnection(className) {
  var cls = className || '';
  if (cls.indexOf('conn-line-yes') !== -1) return 'conn-standby-grad-yes';
  if (cls.indexOf('conn-line-no') !== -1) return 'conn-standby-grad-no';
  if (cls.indexOf('conn-line-light') !== -1) return 'conn-standby-grad-light';
  return 'conn-standby-grad';
}

// Match the dot's peak brightness to its connector's opacity so it never reads
// brighter than the line it rides — mirrors the opacities in WorkflowCanvas.css.
// A "cut" (no-data control-flow) edge is heavily dimmed, so its dot fades to
// match; cut wins over the branch tints since it's the lowest. Pulse stays on,
// just quieter, so direction is still legible.
function pulsePeakForConnection(className) {
  var cls = className || '';
  if (cls.indexOf('conn-line-cut') !== -1) return 0.26;  // .conn-line-cut
  if (cls.indexOf('conn-line-yes') !== -1 || cls.indexOf('conn-line-no') !== -1) return 0.72;
  if (cls.indexOf('conn-line-light') !== -1) return 0.85;
  return 0.9;
}

function buildDot(h, connection, index) {
  var pathD = connection && connection.pathD;
  if (!pathD) return null;
  var begin = travelBegin(index);
  var peak = pulsePeakForConnection(connection.className);
  return h('circle', {
    key: 'standby-dot-' + connection.id,
    className: 'conn-standby-dot',
    r: 0,
    fill: 'url(#' + gradientIdForConnection(connection.className) + ')'
  },
    h('animateMotion', {
      dur: TRAVEL_DUR, begin: begin, repeatCount: 'indefinite',
      path: pathD, calcMode: 'linear', rotate: '0'
    }),
    h('animate', {
      attributeName: 'opacity', dur: TRAVEL_DUR, begin: begin,
      repeatCount: 'indefinite', calcMode: 'linear',
      values: '0;' + peak + ';' + peak + ';0', keyTimes: PULSE_KEYTIMES
    }),
    h('animate', {
      attributeName: 'r', dur: TRAVEL_DUR, begin: begin,
      repeatCount: 'indefinite', calcMode: 'linear',
      values: '1.5;5.5;5.5;1.5', keyTimes: PULSE_KEYTIMES
    })
  );
}

// Connector colors mirrored from WorkflowCanvas.css (.conn-line-yes / -no /
// -light) so the tinted dots match their lines exactly.
var TINTS = [
  { id: 'conn-standby-grad-yes', color: '35, 135, 230' },   // #2387e6 blue (YES)
  { id: 'conn-standby-grad-no', color: '225, 71, 120' },    // #e14778 pink (NO)
  { id: 'conn-standby-grad-light', color: '155, 109, 232' } // #9b6de8 purple (no-wait)
];

function buildTintedGradient(h, tint) {
  return h('radialGradient', { key: tint.id, id: tint.id, cx: '50%', cy: '50%', r: '50%' },
    h('stop', { offset: '0%', stopColor: 'rgb(' + tint.color + ')', stopOpacity: 0.95 }),
    h('stop', { offset: '45%', stopColor: 'rgb(' + tint.color + ')', stopOpacity: 0.45 }),
    h('stop', { offset: '100%', stopColor: 'rgb(' + tint.color + ')', stopOpacity: 0 })
  );
}

// Returns every <radialGradient> the dots reference: a gray default (themed in
// CSS via .conn-standby-core/-mid/-edge for light vs dark canvases) plus one
// fixed-color gradient per connector tint.
function renderStandbyFlowGradient(h) {
  return [
    h('radialGradient', { key: 'conn-standby-grad', id: 'conn-standby-grad', cx: '50%', cy: '50%', r: '50%' },
      h('stop', { offset: '0%', className: 'conn-standby-core' }),
      h('stop', { offset: '45%', className: 'conn-standby-mid' }),
      h('stop', { offset: '100%', className: 'conn-standby-edge' })
    )
  ].concat(TINTS.map(function(tint) { return buildTintedGradient(h, tint); }));
}

// Returns one travelling dot per connection. Caller is responsible for only
// invoking this on standby (not while the workflow is running).
function renderStandbyFlowDots(h, connections) {
  if (!connections || !connections.length) return null;
  return connections.map(function(connection, index) {
    return buildDot(h, connection, index);
  });
}

module.exports = { renderStandbyFlowDots, renderStandbyFlowGradient };
